import React, { useCallback } from 'react';
import { Grid, TextField } from '@material-ui/core';
import { ComponentController, DatePickerElement, InformationMessage } from 'components/common';
import { PayeeFormNames, validateDate, getFormattedDate, MaxAddressLength, MaxPostCodeLength, CountriesWithStatesIso } from './Helpers';
import CountryLookup from './CountryLookup';
import PayeeLookup from './PayeeLookup';
import StateLookup from './StateLookup';
import { AddNewIndividual } from './Popups';

const IndividualPayee = ({
  payeeDetailsState, setPayeeDetailsState, abortController, setAbortController,
  control, forceClearDatasourceState, setForceClearDatasourceState, errors,
  getRequiredError, setBannerState, setValue, reset, resetField, setLoading
}) => {
  const onFieldChange = useCallback((name, value) => {
    setPayeeDetailsState((previous) => {
      return {
        ...previous,
        [name]: value
      }
    });
  }, [setPayeeDetailsState]);

  const renderPostCode = () => {
    const name = "postCode";
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          rules={{ maxLength: MaxPostCodeLength }}
          render={({ field: { name, onBlur, onChange } }) =>
            <TextField
              id={name}
              name={name}
              label={PayeeFormNames[name]}
              fullWidth
              onChange={e => { onFieldChange(name, e.target?.value); onChange(e); }}
              onBlur={onBlur}
              value={payeeDetailsState[name]}
              error={!!errors[name]}
              helperText={<InformationMessage
                fieldName={PayeeFormNames[name]}
                fieldErrors={errors[name]}
                maxLength={MaxPostCodeLength}
                showLengthInfo={true}
                length={payeeDetailsState[name]?.length}
              />}
            />
          }
        />
      </Grid>
    );
  }

  const renderAddress = () => {
    const name = "address";
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          rules={{ maxLength: MaxAddressLength }}
          render={({ field: { name, onBlur, onChange } }) =>
            <TextField
              id={name}
              name={name}
              label={PayeeFormNames[name]}
              fullWidth
              onChange={e => { onFieldChange(name, e.target?.value); onChange(e); }}
              onBlur={onBlur}
              value={payeeDetailsState[name]}
              error={!!errors[name]}
              helperText={<InformationMessage
                fieldName={PayeeFormNames[name]}
                fieldErrors={errors[name]}
                maxLength={MaxAddressLength}
                showLengthInfo={true}
                length={payeeDetailsState[name]?.length}
              />}
            />
          }
        />
      </Grid>
    );
  }

  const onDateOfBirthChange = useCallback((date) => {
    let dateOfBirth = validateDate(date) ? getFormattedDate(date) : null;

    setPayeeDetailsState((previous) => {
      return {
        ...previous,
        dateOfBirth: dateOfBirth ?? date,
      };
    });
  }, [setPayeeDetailsState]);

  const getDateValidationError = name => {
    let validDate = errors[name]?.type === 'validDate' &&
      <span role="alert">"{PayeeFormNames[name]}" is not valid</span>;

    return validDate;
  }

  const renderDateOfBirth = () => {
    const name = "dateOfBirth";
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          rules={
            {
              validate: { validDate: value => validateDate(value) }
            }}
          render={({ field: { name, onChange, onBlur } }) =>
            <DatePickerElement
              id={name}
              name={name}
              label={PayeeFormNames[name]}
              fullWidth
              format="dd/MM/yyyy"
              value={payeeDetailsState[name]}
              onChange={e => { onDateOfBirthChange(e); onChange(e); }}
              onBlur={onBlur}
              error={!!errors[name]}
              helperText={getDateValidationError(name)}
              maxDate={new Date()}
            />
          }
        />
      </Grid>
    );
  }

  const renderCountry = () => {
    return (
      <CountryLookup
        countryFieldName="countryOfResidency"
        payeeDetailsState={payeeDetailsState}
        setPayeeDetailsState={setPayeeDetailsState}
        abortController={abortController}
        setAbortController={setAbortController}
        control={control}
        forceClearDatasourceState={forceClearDatasourceState}
        setForceClearDatasourceState={setForceClearDatasourceState}
        errors={errors}
        getRequiredError={getRequiredError}
        setBannerState={setBannerState}
        setValue={setValue}
        reset={reset}
        resetField={resetField}
        setLoading={setLoading}
      />
    );
  }

  const renderState = () => {
    const render = CountriesWithStatesIso.includes(payeeDetailsState.countryOfResidencyIso2);
    return (
      render &&
      <StateLookup
        stateFieldName="stateOfResidency"
        payeeDetailsState={payeeDetailsState}
        setPayeeDetailsState={setPayeeDetailsState}
        abortController={abortController}
        setAbortController={setAbortController}
        control={control}
        forceClearDatasourceState={forceClearDatasourceState}
        setForceClearDatasourceState={setForceClearDatasourceState}
        errors={errors}
        getRequiredError={getRequiredError}
        setBannerState={setBannerState}
        setValue={setValue}
        reset={reset}
        resetField={resetField}
        setLoading={setLoading}
      />
    );
  }

  const renderIndividualLookupHeader = () => {
    return ({ className }) => {
      return (
        <Grid container direction="row" spacing={0} className={className}>
          <Grid item xs={4}><div>Payee</div></Grid>
          <Grid item xs={1}><div>Country</div></Grid>
          <Grid item xs={1}><div>State</div></Grid>
          <Grid item xs={1}><div>City</div></Grid>
          <Grid item xs={4}><div>Address</div></Grid>
          <Grid item xs={1}><div>Source</div></Grid>
        </Grid>
      );
    };
  }

  const renderIndividualLookupView = () => { //TODO: use name from API
    return ({ option, className }) => {
      return (
        <Grid container direction="row" spacing={0} className={className}>
          <Grid item xs={4}><div>{option.organizationName}</div></Grid>
          <Grid item xs={1}><div>{option.countryOfResidency}</div></Grid>
          <Grid item xs={1}><div>{option.stateCode}</div></Grid>
          <Grid item xs={1}><div>{option.city}</div></Grid>
          <Grid item xs={4}><div>{`${option.address ?? ''} ${option.postcode ?? ''}`}</div></Grid>
          <Grid item xs={1}><div>{option.source}</div></Grid>
        </Grid>
      );
    };
  }

  const renderPayeeLookup = () => {
    return (
      <PayeeLookup
        payeeDetailsState={payeeDetailsState}
        setPayeeDetailsState={setPayeeDetailsState}
        abortController={abortController}
        setAbortController={setAbortController}
        control={control}
        forceClearDatasourceState={forceClearDatasourceState}
        setForceClearDatasourceState={setForceClearDatasourceState}
        errors={errors}
        getRequiredError={getRequiredError}
        setBannerState={setBannerState}
        setValue={setValue}
        setLoading={setLoading}
        renderLookupHeader={renderIndividualLookupHeader}
        renderLookupView={renderIndividualLookupView}
        Popup={AddNewIndividual}
      />
    )
  }

  return (
    <>
      {renderDateOfBirth()}
      {renderAddress()}
      {renderPostCode()}
      {renderCountry()}
      {renderState()}
      {renderPayeeLookup()}
    </>
  );
}

export default IndividualPayee;
